import { useCallback, useContext, useState } from "react";
import { Stack } from "@mui/material";
import { useTranslation } from "react-i18next";
import { v4 } from "uuid";

import type {
  ExtendedPatient,
  SuccessPatientIndexSearchResultV2,
  TooManyMatchesPatientIndexSearchResultV2,
} from "@thalamos/aspire-common";

import { CollectCriteria } from "../PatientSearchV2/CollectCriteria.js";
import type { CollectCriteriaDemographicsProps } from "../PatientSearchV2/CollectCriteriaDemographics.js";
import type { CollectCriteriaNhsNumberProps } from "../PatientSearchV2/CollectCriteriaNhsNumber.js";
import { SearchResults } from "../PatientSearchV2/SearchResults.js";
import { api } from "~/api.js";
import {
  Banner,
  BannerList,
  Button,
  renderErrorToast,
} from "~/components/design-system/index.js";
import { FormFooterSection } from "~/components/form/FormFooter.js";
import { LoggedInUserContext } from "~/Contexts.js";

export type SoadNhsNumberSearchProps = {
  onPatientSelected: (patient: ExtendedPatient) => Promise<void>;
  setProceedManualInputPopup: (value: boolean) => void;
  setShowPatientDetails: (value: boolean) => void;
};

type SearchResultsAndHandlers = {
  searchResults:
    | "loading"
    | TooManyMatchesPatientIndexSearchResultV2
    | SuccessPatientIndexSearchResultV2;

  /**
   * The function we will call if we want to continue with a search result.
   * @param index The index of the selected search result.
   * @returns
   */
  onContinue?: (index: number) => void;
};

export function SoadNhsNumberSearch({
  onPatientSelected,
  setProceedManualInputPopup,
  setShowPatientDetails,
}: SoadNhsNumberSearchProps) {
  const { t } = useTranslation();

  const userContext = useContext(LoggedInUserContext);
  const limitedToPdsPatientNhsNumber =
    userContext?.user.sessionOrganisationConfiguration
      ?.pdsAllowedPatientNhsNumber ?? undefined;

  const [searchResultsAndHandlers, setSearchResultsAndHandlers] = useState<
    SearchResultsAndHandlers | undefined
  >(undefined);

  const handleSearchResultIndexSelected = useCallback(
    (index: number | null) => {
      if (index !== null) {
        searchResultsAndHandlers?.onContinue?.(index);
        setShowPatientDetails(true);
      }
    },
    [searchResultsAndHandlers, setShowPatientDetails],
  );

  const state =
    searchResultsAndHandlers === undefined
      ? "search"
      : searchResultsAndHandlers.searchResults === "loading"
        ? "searching"
        : searchResultsAndHandlers.searchResults.searchOutcome ===
            "too-many-matches"
          ? "too-many-matches"
          : searchResultsAndHandlers.searchResults.pdsResult ===
              "too-many-matches"
            ? "too-many-matches-pds"
            : searchResultsAndHandlers.searchResults.matchedPatients.length ===
                0
              ? "results-not-found"
              : "results-found";
  const tooManyMatches = state === "too-many-matches";

  const onContinueFn =
    (data: SuccessPatientIndexSearchResultV2) => async (index: number) => {
      const patient = data.matchedPatients[index];
      if (patient === undefined) return;

      const searchReasoningResult = await api.patients.addSearchV2Reasoning(
        patient.resultId,
        {
          reason: "SOAD assessment",
        },
      );

      if (searchReasoningResult.status !== 200) {
        renderErrorToast({
          message: "Failed to add search reasoning - please try again later",
        });
        return;
      }

      if (
        searchReasoningResult.data.type === "patient-page" ||
        searchReasoningResult.data.type === "s-flag-setup"
      ) {
        const patientGetResult = await api.patients.get(
          searchReasoningResult.data.patientId,
        );
        if (patientGetResult.status !== 200) {
          renderErrorToast({
            message: "Failed to get patient - please try again later",
          });
          return;
        }

        const patient = patientGetResult.data;

        if (!patient) {
          renderErrorToast({
            message: "Failed to get patient - please try again later",
          });
          return;
        }

        await onPatientSelected(patient);
      }
    };

  // Search for patients using NHS Number
  const searchByNhsNumber: CollectCriteriaNhsNumberProps["onSubmit"] = async (
    values,
  ) => {
    setSearchResultsAndHandlers({ searchResults: "loading" });

    const searchResponse = await api.patients
      .searchV2(v4(), {
        type: "nhs-number",
        nhsNumber: values.nhsNumber,
        patientSearchVariant: "normal",
      })
      .catch(() => undefined);

    if (searchResponse?.status !== 200) {
      setSearchResultsAndHandlers(undefined);
      renderErrorToast({
        message: "Failed to search for patients - please try again later",
      });
      return;
    }

    const { data } = searchResponse;
    if (data.searchOutcome === "too-many-matches") {
      setSearchResultsAndHandlers({ searchResults: data });
      return;
    }
    setSearchResultsAndHandlers({
      searchResults: data,
      onContinue: onContinueFn(data),
    });
  };

  const searchByDemographics: CollectCriteriaDemographicsProps["onSubmit"] =
    async (_values) => {};

  return (
    <Stack>
      {limitedToPdsPatientNhsNumber && (
        <Banner
          bannerType={BannerList.WARNING}
          body={t("pages.soadAssessment.nhsNumberSearch.nhsNumberConstraint", {
            nhsNumber: limitedToPdsPatientNhsNumber,
          })}
          sx={{ mb: 3 }}
        />
      )}
      <CollectCriteria
        lastSearchedDemographics={undefined}
        canUsePhoneticNameMatch={false}
        onSubmitNhsNumber={searchByNhsNumber}
        onSubmitDemographics={searchByDemographics}
        onSubmitPdsDemographics={() => {}}
        criteriaType={"nhs-number"}
        disabled={["searching", "result-selected"].includes(state)}
      >
        {({ onClear }) => {
          return (
            <>
              <SearchResults
                canSearchByPdsDemographics={false}
                criteriaType={"nhs-number"}
                setCriteriaType={() => {}}
                searchResult={
                  tooManyMatches
                    ? "too-many-matches"
                    : (searchResultsAndHandlers?.searchResults as SuccessPatientIndexSearchResultV2)
                }
                onSelectedIndexChanged={handleSearchResultIndexSelected}
                selectionComponent="button"
                continueInstruction={t(
                  "pages.soadAssessment.nhsNumberSearch.continueInstruction",
                )}
                alternativeInstruction={t(
                  "pages.soadAssessment.nhsNumberSearch.alternativeInstruction",
                )}
              />
              <FormFooterSection
                disableSubmit={["searching"].includes(state)}
                secondaryLeftButton={
                  [
                    "results-not-found",
                    "too-many-matches",
                    "too-many-matches-pds",
                    "results-found",
                  ].includes(state) ? (
                    <Button
                      onClick={() => {
                        setProceedManualInputPopup(true);
                      }}
                      label={t("buttonLabels.inputDetails")}
                      variant={"outlined"}
                    />
                  ) : undefined
                }
                saveLabel={
                  [
                    "search",
                    "searching",
                    "results-not-found",
                    "too-many-matches",
                    "too-many-matches-pds",
                    "results-found",
                  ].includes(state)
                    ? t("buttonLabels.search")
                    : t("buttonLabels.continue")
                }
                hideSubmit={["results-found"].includes(state)}
                discardLabel="Clear Search"
                discardColor="error"
                performSaveActionOnFormSubmit={[
                  "search",
                  "results-not-found",
                  "results-found",
                  "too-many-matches",
                  "too-many-matches-pds",
                ].includes(state)}
                onSave={() => {}}
                onCancel={() => {
                  setSearchResultsAndHandlers(undefined);
                  onClear();
                }}
              />
            </>
          );
        }}
      </CollectCriteria>
    </Stack>
  );
}
